/*
 * NOTE TASK LIST DIALOG
 * Purpose: Show persisted tasks linked to a note and expose quick workflow actions.
 * Implementation notes: Uses shared task workflow labels and keeps cancel actions inside the visible task status flow.
 * Recent changes:
 * - Requires a valid due date before saving an editable note-linked task.
 * - Replaced delete-panel actions with cancel status updates and aligned labels to the task board workflow.
 * - Uses the shared wide task-popup layout and joined title-and-notes control for edit and view.
 */

import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { THEME_CLASSES } from '../../constants/themeConstants';
import {
  TASK_DUE_DATE_PAST_ERROR,
  TASK_DUE_DATE_REQUIRED_ERROR,
  canMutateTask,
  getTaskDueDateValidationError,
  getTaskStatusLabel,
  getTodayDateValue,
  isTaskClosed,
  type Task,
  type TaskPriority,
  type TaskStatus,
} from '../../models/Task';
import { updateTask } from '../../services/taskService';
import { Button, DialogShell } from '../primitives';
import {
  TaskDueDateField,
  TaskPopupFooter,
  TaskPopupLayout,
  TaskPriorityControl,
  TaskTextFields,
  TASK_POPUP_CLOSE_CLASS_NAME,
  TASK_POPUP_HEADER_CLASS_NAME,
  TASK_POPUP_TITLE_CLASS_NAME,
} from '../task/popup';

interface NoteTaskListDialogProps {
  open: boolean;
  noteId: number;
  tasks: Task[];
  currentUserId: number | null;
  onClose: () => void;
  onCompleteTask: (taskId: number) => void;
  onCancelTask: (taskId: number) => void;
}

const STATUS_STYLES: Record<Task['status'], string> = {
  Open: 'bg-amber-100 text-amber-800',
  Waiting: 'bg-sky-100 text-sky-800',
  Done: 'bg-emerald-100 text-emerald-800',
  Canceled: 'bg-slate-200 text-slate-700'
};

const formatDueDate = (dueDate: string | null) => {
  if (!dueDate) {
    return 'No due date';
  }

  const parsedDate = new Date(`${dueDate}T00:00:00`);
  if (Number.isNaN(parsedDate.getTime())) {
    return dueDate;
  }

  return parsedDate.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  });
};

const NoteTaskListDialog: React.FC<NoteTaskListDialogProps> = ({
  open,
  noteId,
  tasks,
  currentUserId,
  onClose,
  onCompleteTask,
  onCancelTask,
}) => {
  const [selectedTaskId, setSelectedTaskId] = useState<number | null>(null);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [status, setStatus] = useState<TaskStatus>('Open');
  const [priority, setPriority] = useState<TaskPriority>('Medium');
  const [dueDate, setDueDate] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const minDueDate = useMemo(() => getTodayDateValue(), []);
  const selectedTask = useMemo(
    () => selectedTaskId === null ? null : tasks.find((task) => task.id === selectedTaskId) ?? null,
    [selectedTaskId, tasks]
  );
  const canEditTask = selectedTask ? canMutateTask(selectedTask, currentUserId) : false;

  useEffect(() => {
    if (!open) {
      setSelectedTaskId(null);
      setError(null);
      setIsSaving(false);
    }
  }, [open]);

  useEffect(() => {
    if (!selectedTask) {
      return;
    }

    setTitle(selectedTask.title);
    setDescription(selectedTask.description);
    setStatus(selectedTask.status);
    setPriority(selectedTask.priority);
    setDueDate(selectedTask.dueDate ?? '');
    setError(null);
  }, [selectedTask]);

  const handleCloseTaskPanel = () => {
    setSelectedTaskId(null);
    setError(null);
    setIsSaving(false);
  };

  const handleOpenTaskPanel = (task: Task) => {
    setSelectedTaskId(task.id);
  };

  const handleSaveTask = async () => {
    if (!selectedTask || !canEditTask) {
      handleCloseTaskPanel();
      return;
    }

    if (!title.trim()) {
      setError('Task title is required.');
      return;
    }

    const dueDateValidationError = getTaskDueDateValidationError(dueDate, minDueDate);
    if (dueDateValidationError) {
      setError(dueDateValidationError);
      return;
    }

    setIsSaving(true);
    setError(null);

    try {
      await updateTask(selectedTask.id, {
        title: title.trim(),
        description: description.trim(),
        status,
        priority,
        dueDate: dueDate || null,
      });

      toast.success(`Task "${title.trim()}" updated.`);
      handleCloseTaskPanel();
    } catch (saveError) {
      console.error('Failed to update note-linked task:', saveError);
      setError('Unable to update this task right now.');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <>
      <DialogShell
        open={open}
        title={`Task List for Note #${noteId}`}
        onClose={onClose}
        panelClassName="max-w-3xl"
        bodyClassName="p-0"
      >
        <div className="max-h-[70vh] overflow-y-auto">
          {tasks.length > 0 ? (
            <div className="divide-y divide-slate-200 dark:divide-slate-800">
              {tasks.map((task) => {
                const isClosedTask = isTaskClosed(task.status);

                return (
                  <div
                    key={task.id}
                    onDoubleClick={() => handleOpenTaskPanel(task)}
                    className="flex cursor-pointer flex-col gap-4 px-6 py-5 transition-colors hover:bg-slate-50 dark:hover:bg-slate-900/50 sm:flex-row sm:items-start sm:justify-between"
                  >
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <h4 className={`text-sm font-semibold ${THEME_CLASSES.text.primary}`}>{task.title}</h4>
                        <span className={`inline-flex rounded-full px-2.5 py-1 text-[11px] font-semibold ${STATUS_STYLES[task.status]}`}>
                          {getTaskStatusLabel(task.status)}
                        </span>
                      </div>
                      {task.description && (
                        <p className={`mt-2 text-sm ${THEME_CLASSES.text.secondary}`}>{task.description}</p>
                      )}
                      <dl className="mt-3 grid gap-2 text-xs sm:grid-cols-2">
                        <div>
                          <dt className={`font-semibold uppercase tracking-[0.14em] ${THEME_CLASSES.text.secondary}`}>Assignee</dt>
                          <dd className={`mt-1 ${THEME_CLASSES.text.primary}`}>{task.assigneeName}</dd>
                        </div>
                        <div>
                          <dt className={`font-semibold uppercase tracking-[0.14em] ${THEME_CLASSES.text.secondary}`}>Due date</dt>
                          <dd className={`mt-1 ${THEME_CLASSES.text.primary}`}>{formatDueDate(task.dueDate)}</dd>
                        </div>
                        <div>
                          <dt className={`font-semibold uppercase tracking-[0.14em] ${THEME_CLASSES.text.secondary}`}>Account</dt>
                          <dd className={`mt-1 ${THEME_CLASSES.text.primary}`}>{task.accountName}</dd>
                        </div>
                        <div>
                          <dt className={`font-semibold uppercase tracking-[0.14em] ${THEME_CLASSES.text.secondary}`}>Contact</dt>
                          <dd className={`mt-1 ${THEME_CLASSES.text.primary}`}>{task.contactName || '-'}</dd>
                        </div>
                      </dl>
                    </div>

                    <div className="flex shrink-0 items-center gap-3">
                      {!isClosedTask ? (
                        <>
                          <Button
                            onClick={(event) => {
                              event.stopPropagation();
                              onCompleteTask(task.id);
                            }}
                            size="sm"
                          >
                            Complete Task
                          </Button>
                          <Button
                            onClick={(event) => {
                              event.stopPropagation();
                              onCancelTask(task.id);
                            }}
                            size="sm"
                            variant="secondary"
                          >
                            Cancel Task
                          </Button>
                        </>
                      ) : (
                        <span className={`text-xs font-medium ${THEME_CLASSES.text.secondary}`}>
                          {getTaskStatusLabel(task.status)}
                        </span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className={`px-6 py-12 text-center text-sm ${THEME_CLASSES.text.secondary}`}>
              No linked tasks found for this note.
            </div>
          )}
        </div>
      </DialogShell>

      <DialogShell
        open={selectedTask !== null}
        title={selectedTask ? (canEditTask ? 'Edit Task' : 'View Task') : 'Task'}
        onClose={handleCloseTaskPanel}
        panelClassName="!w-[calc(100vw-1.5rem)] !max-w-5xl !rounded-2xl sm:!w-[calc(100vw-3rem)]"
        headerClassName={TASK_POPUP_HEADER_CLASS_NAME}
        titleClassName={TASK_POPUP_TITLE_CLASS_NAME}
        closeButtonClassName={TASK_POPUP_CLOSE_CLASS_NAME}
        bodyPadding={false}
      >
        {selectedTask && (
          <div className="p-5 sm:p-6">
            <TaskPopupLayout
              primary={(
                <TaskTextFields
                  idPrefix="note-task-panel"
                  title={title}
                  notes={description}
                  onTitleChange={(value) => {
                    setTitle(value);
                    if (error === 'Task title is required.' && value.trim()) setError(null);
                  }}
                  onNotesChange={setDescription}
                  disabled={!canEditTask}
                  invalid={error === 'Task title is required.'}
                  notesRows={10}
                />
              )}
              details={(
                <>
                  <TaskPriorityControl idPrefix="note-task-panel" value={priority} onChange={setPriority} disabled={!canEditTask} />

                  <div className={`rounded-xl border ${THEME_CLASSES.card.border} ${THEME_CLASSES.card.background} px-4 py-3 text-sm`}>
                    <div className={`text-xs font-semibold uppercase tracking-[0.12em] ${THEME_CLASSES.text.secondary}`}>Assignee</div>
                    <div className={`mt-2 ${THEME_CLASSES.text.primary}`}>{selectedTask.assigneeName}</div>
                  </div>

                  <TaskDueDateField
                    id="note-task-panel-due-date"
                    min={minDueDate}
                    value={dueDate}
                    onChange={(value) => {
                      setDueDate(value);
                      if ((error === TASK_DUE_DATE_REQUIRED_ERROR || error === TASK_DUE_DATE_PAST_ERROR) && !getTaskDueDateValidationError(value, minDueDate)) setError(null);
                    }}
                    disabled={!canEditTask}
                    invalid={error === TASK_DUE_DATE_REQUIRED_ERROR || error === TASK_DUE_DATE_PAST_ERROR}
                  />

                  <div>
                    <label className={`mb-2 block text-xs font-semibold uppercase tracking-[0.12em] ${THEME_CLASSES.text.secondary}`} htmlFor="note-task-panel-status">Status</label>
                    <select
                      id="note-task-panel-status"
                      value={status}
                      onChange={(event) => setStatus(event.target.value as TaskStatus)}
                      className={`w-full rounded-xl border ${THEME_CLASSES.input.border} ${THEME_CLASSES.input.background} ${THEME_CLASSES.input.text} px-3 py-2 text-sm`}
                      disabled={!canEditTask}
                    >
                      {(['Open', 'Waiting', 'Done', 'Canceled'] as TaskStatus[]).map((taskStatus) => (
                        <option key={taskStatus} value={taskStatus}>{getTaskStatusLabel(taskStatus)}</option>
                      ))}
                    </select>
                  </div>
                </>
              )}
            />

            {error && (
              <div className="mt-5 rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700 dark:border-rose-900 dark:bg-rose-950/40 dark:text-rose-300">
                {error}
              </div>
            )}

            {!canEditTask && (
              <div className={`mt-5 rounded-xl border ${THEME_CLASSES.card.border} ${THEME_CLASSES.card.background} px-4 py-3 text-sm ${THEME_CLASSES.text.secondary}`}>
                You can view this task, but only the creator or an assignee can edit it.
              </div>
            )}

            <TaskPopupFooter
              showNotification={false}
              actions={(
                <>
                  <Button onClick={handleCloseTaskPanel} variant="secondary" size="sm">{canEditTask ? 'Cancel' : 'Close'}</Button>
                  {canEditTask && <Button onClick={handleSaveTask} size="sm" disabled={isSaving}>{isSaving ? 'Saving...' : 'Save Task'}</Button>}
                </>
              )}
            />
          </div>
        )}
      </DialogShell>
    </>
  );
};

export default NoteTaskListDialog;
